import { API_URL, GOOGLE_CLIENT_ID, TURNSTILE_SITE_KEY, APP_VERSION } from '../config';
import { useRef, useState } from 'react';
import axios from 'axios';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { usuariosApi, AuthUser } from '../api/api';
import Lottie from 'lottie-react';
import callCenterAnim from '../assets/lottie-call-center.json';
import { formatCpf, isValidCpf, normalizeCpf } from '../utils/cpf';

const api = axios.create({ baseURL: API_URL });

type Mode = 'login' | 'cadastro';
interface Props { onLogin: (user: AuthUser) => void; }

// ── Icons ──────────────────────────────────────────────────────────────────────

const MailIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-7 4h8m2 5H6a2 2 0 01-2-2V5a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Field input ────────────────────────────────────────────────────────────────

function Field({
  icon,
  right,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  icon: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="relative">
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
        {icon}
      </span>
      <input
        {...props}
        className="field-input w-full h-11 bg-white border border-gray-200 rounded-lg pl-10 pr-11 text-sm text-gray-900 placeholder-gray-400"
      />
      {right && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
          {right}
        </div>
      )}
    </div>
  );
}

// ── Turnstile (Cloudflare anti-bot) ───────────────────────────────────────────

function TurnstileBlock({
  onToken,
  tsRef,
}: {
  onToken: (t: string) => void;
  tsRef: React.MutableRefObject<TurnstileInstance | null>;
}) {
  if (!TURNSTILE_SITE_KEY) return null;
  return (
    <div className="w-full">
      <Turnstile
        ref={tsRef}
        siteKey={TURNSTILE_SITE_KEY}
        onSuccess={onToken}
        onError={() => onToken('')}
        onExpire={() => onToken('')}
        options={{ theme: 'light', size: 'flexible' as any }}
      />
    </div>
  );
}

// ── Google sign-in ─────────────────────────────────────────────────────────────

interface GoogleProfilePrompt {
  idToken: string;
  email: string;
  suggestedName: string;
  picture: string | null;
}

function GoogleAuthBlock({
  onLogin,
  onError,
  onNeedsProfile,
  mode,
}: {
  onLogin: (user: AuthUser) => void;
  onError: (msg: string) => void;
  onNeedsProfile: (prompt: GoogleProfilePrompt) => void;
  mode: Mode;
}) {
  const [loading, setLoading] = useState(false);

  if (!GOOGLE_CLIENT_ID) return null;

  const handleCredential = async (resp: CredentialResponse) => {
    if (!resp.credential) { onError('Falha ao obter credencial do Google'); return; }
    setLoading(true);
    try {
      const { data } = await usuariosApi.googleLogin(resp.credential);
      if ('needsProfile' in data && data.needsProfile) {
        onNeedsProfile({
          idToken: resp.credential,
          email: data.email,
          suggestedName: data.suggestedName,
          picture: data.picture,
        });
      } else {
        onLogin(data as AuthUser);
      }
    } catch (err: any) {
      onError(err?.response?.data?.message || 'Nao foi possivel entrar com Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-center" style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
        <GoogleLogin
          onSuccess={handleCredential}
          onError={() => onError('Falha ao autenticar com Google')}
          text={mode === 'login' ? 'signin_with' : 'signup_with'}
          shape="rectangular"
          width="384"
          locale="pt-BR"
        />
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-[11px] uppercase tracking-wider text-gray-400">ou</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
    </div>
  );
}

// ── Google CPF/nome modal (segundo passo do cadastro via Google) ──────────────

function GoogleProfileModal({
  prompt,
  onClose,
  onLogin,
}: {
  prompt: GoogleProfilePrompt;
  onClose: () => void;
  onLogin: (user: AuthUser) => void;
}) {
  const [nome, setNome] = useState(prompt.suggestedName);
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) { setError('Informe seu nome completo'); return; }
    if (!normalizeCpf(cpf)) { setError('Informe seu CPF'); return; }
    if (!isValidCpf(cpf)) { setError('Informe um CPF válido'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await usuariosApi.googleLogin(prompt.idToken, {
        cpf: normalizeCpf(cpf),
        nome_completo: nome.trim(),
      });
      if ('needsProfile' in data && data.needsProfile) {
        setError('Não foi possível concluir o cadastro. Tente novamente.');
      } else {
        onLogin(data as AuthUser);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao concluir cadastro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)' }}
    >
      <div className="w-full max-w-sm bg-white rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          {prompt.picture && (
            <img src={prompt.picture} alt="" className="w-10 h-10 rounded-full" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">Concluir cadastro</p>
            <p className="text-xs text-gray-500 truncate">{prompt.email}</p>
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Para criar sua conta, confirme seu nome e informe seu CPF.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <Field
            type="text"
            value={nome}
            onChange={e => { setNome(e.target.value); setError(''); }}
            placeholder="Nome completo"
            icon={<UserIcon />}
            autoFocus
          />
          <Field
            type="text"
            value={cpf}
            onChange={e => { setCpf(formatCpf(e.target.value)); setError(''); }}
            placeholder="CPF"
            inputMode="numeric"
            maxLength={14}
            icon={<DocumentIcon />}
          />

          {error && <ErrorBanner message={error} />}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 h-11 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-11 flex items-center justify-center gap-2 text-white text-sm font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 transition-all"
              style={{ boxShadow: '0 1px 10px rgba(16,185,129,0.22)' }}
            >
              {loading ? <><Spinner /> Criando...</> : 'Criar conta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Error banner ───────────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5">
      <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="text-xs text-red-600">{message}</p>
    </div>
  );
}

// ── Login form ─────────────────────────────────────────────────────────────────

function LoginForm({
  onLogin,
  onSwitchMode,
  onNeedsProfile,
}: {
  onLogin: (user: AuthUser) => void;
  onSwitchMode: () => void;
  onNeedsProfile: (prompt: GoogleProfilePrompt) => void;
}) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const tsRef = useRef<TurnstileInstance | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !senha.trim()) { setError('Preencha todos os campos'); return; }
    if (TURNSTILE_SITE_KEY && !turnstileToken) { setError('Aguarde a verificação anti-bot'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/usuarios/login', { email: email.trim(), senha, turnstileToken });
      onLogin(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'E-mail ou senha inválidos');
      setTurnstileToken('');
      tsRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-3.5" noValidate>
      <GoogleAuthBlock mode="login" onLogin={onLogin} onError={setError} onNeedsProfile={onNeedsProfile} />

      <Field
        type="email"
        value={email}
        onChange={e => { setEmail(e.target.value); setError(''); }}
        placeholder="seu@email.com"
        autoFocus
        icon={<MailIcon />}
      />

      <div className="space-y-1.5">
        <Field
          type={showSenha ? 'text' : 'password'}
          value={senha}
          onChange={e => { setSenha(e.target.value); setError(''); }}
          placeholder="Sua senha"
          icon={<LockIcon />}
          right={
            <button
              type="button"
              onClick={() => setShowSenha(v => !v)}
              tabIndex={-1}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showSenha ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          }
        />
        <div className="flex justify-end">
          <button
            type="button"
            className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors font-medium"
          >
            Esqueci minha senha
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <button
        type="submit"
        disabled={loading}
        className="w-full h-11 flex items-center justify-center gap-2 text-white text-sm font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        style={{ boxShadow: '0 1px 10px rgba(16,185,129,0.22)' }}
      >
        {loading ? <><Spinner /> Entrando...</> : 'Entrar'}
      </button>

      <TurnstileBlock onToken={setTurnstileToken} tsRef={tsRef} />

      {/* Criar conta link */}
      <p className="text-center text-sm text-gray-400 pt-1">
        Não tem conta?{' '}
        <button
          type="button"
          onClick={onSwitchMode}
          className="font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
        >
          Criar conta grátis
        </button>
      </p>
    </form>
  );
}

// ── Cadastro form ──────────────────────────────────────────────────────────────

function CadastroForm({
  onLogin,
  onSwitchMode,
  onNeedsProfile,
}: {
  onLogin: (user: AuthUser) => void;
  onSwitchMode: () => void;
  onNeedsProfile: (prompt: GoogleProfilePrompt) => void;
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const tsRef = useRef<TurnstileInstance | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) { setError('Informe seu nome completo'); return; }
    if (!email.trim()) { setError('Informe seu e-mail'); return; }
    if (!normalizeCpf(cpf)) { setError('Informe seu CPF'); return; }
    if (!isValidCpf(cpf)) { setError('Informe um CPF válido'); return; }
    if (senha.length < 6) { setError('A senha deve ter pelo menos 6 caracteres'); return; }
    if (TURNSTILE_SITE_KEY && !turnstileToken) { setError('Aguarde a verificação anti-bot'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await usuariosApi.registrar(nome.trim(), email.trim(), normalizeCpf(cpf), senha, turnstileToken);
      onLogin(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao criar conta. Tente novamente.');
      setTurnstileToken('');
      tsRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-3.5" noValidate>
      <GoogleAuthBlock mode="cadastro" onLogin={onLogin} onError={setError} onNeedsProfile={onNeedsProfile} />

      <Field
        type="text"
        value={nome}
        onChange={e => { setNome(e.target.value); setError(''); }}
        placeholder="Seu nome completo"
        autoFocus
        icon={<UserIcon />}
      />
      <Field
        type="email"
        value={email}
        onChange={e => { setEmail(e.target.value); setError(''); }}
        placeholder="seu@email.com"
        icon={<MailIcon />}
      />
      <Field
        type="text"
        value={cpf}
        onChange={e => { setCpf(formatCpf(e.target.value)); setError(''); }}
        placeholder="CPF"
        inputMode="numeric"
        maxLength={14}
        icon={<DocumentIcon />}
      />
      <Field
        type={showSenha ? 'text' : 'password'}
        value={senha}
        onChange={e => { setSenha(e.target.value); setError(''); }}
        placeholder="Senha (mín. 6 caracteres)"
        icon={<LockIcon />}
        right={
          <button
            type="button"
            onClick={() => setShowSenha(v => !v)}
            tabIndex={-1}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showSenha ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        }
      />

      {error && <ErrorBanner message={error} />}

      <button
        type="submit"
        disabled={loading}
        className="w-full h-11 flex items-center justify-center gap-2 text-white text-sm font-semibold rounded-lg bg-emerald-500 hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        style={{ boxShadow: '0 1px 10px rgba(16,185,129,0.22)' }}
      >
        {loading ? <><Spinner /> Criando conta...</> : 'Criar conta'}
      </button>

      <TurnstileBlock onToken={setTurnstileToken} tsRef={tsRef} />

      <p className="text-center text-sm text-gray-400 pt-1">
        Já tem conta?{' '}
        <button
          type="button"
          onClick={onSwitchMode}
          className="font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
        >
          Entrar
        </button>
      </p>
    </form>
  );
}

// ── Logo ───────────────────────────────────────────────────────────────────────

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <span className="font-bold text-base text-gray-900 tracking-tight">Disparador Pro</span>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

const BULLETS: { text: string }[] = [
  { text: 'Disparo em massa via WhatsApp' },
  { text: 'Bases de contatos organizadas' },
  { text: 'Templates e gestão de leads' },
];

export default function LoginPage({ onLogin }: Props) {
  const [mode, setMode] = useState<Mode>('login');
  const [animating, setAnimating] = useState(false);
  const [googlePrompt, setGooglePrompt] = useState<GoogleProfilePrompt | null>(null);

  const switchMode = (next: Mode) => {
    if (next === mode || animating) return;
    setAnimating(true);
    setTimeout(() => { setMode(next); setAnimating(false); }, 200);
  };

  const isLogin = mode === 'login';

  const fadeStyle = {
    transition: 'opacity 200ms ease, transform 200ms ease',
    opacity: animating ? 0 : 1,
    transform: animating ? 'translateY(5px)' : 'translateY(0)',
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#F1F5F9' }}>
      {googlePrompt && (
        <GoogleProfileModal
          prompt={googlePrompt}
          onClose={() => setGooglePrompt(null)}
          onLogin={(u) => { setGooglePrompt(null); onLogin(u); }}
        />
      )}
      <div className="login-enter w-full flex">

        {/* ── LEFT PANEL — formulário ─────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col items-center justify-center px-12 py-10 bg-white relative">
          <div className="w-full max-w-sm text-center">
            <div
              style={{
                transition: 'opacity 200ms ease, transform 200ms ease',
                opacity: animating ? 0 : 1,
                transform: animating ? 'translateY(-5px)' : 'translateY(0)',
              }}
            >
              <div className="mb-7 text-center">
                <h1 className="text-xl font-bold text-gray-900 leading-tight">
                  {isLogin ? 'Entrar na sua conta' : 'Criar sua conta'}
                </h1>
              </div>
              <div className="text-left">
                {isLogin
                  ? <LoginForm onLogin={onLogin} onSwitchMode={() => switchMode('cadastro')} onNeedsProfile={setGooglePrompt} />
                  : <CadastroForm onLogin={onLogin} onSwitchMode={() => switchMode('login')} onNeedsProfile={setGooglePrompt} />
                }
              </div>
            </div>
          </div>
          <p className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-400">
            desenvolvido por{' '}
            <a
              href="https://rootfycode.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              Rootfy Code
            </a>
            <span className="mx-1.5 text-gray-300">·</span>
            <span className="text-gray-400">v{APP_VERSION}</span>
          </p>
        </div>

        {/* ── RIGHT PANEL — decorativo ─────────────────────────────────────────── */}
        <div
          className="hidden md:flex flex-col relative overflow-hidden"
          style={{
            width: '58%',
            flexShrink: 0,
            background: 'linear-gradient(160deg, #ECFDF5 0%, #F0FDF4 50%, #F8FAFC 100%)',
            borderLeft: '1px solid #E2E8F0',
          }}
        >
          {/* Decorative shapes */}
          <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(16,185,129,0.07)' }} />
          <div style={{ position: 'absolute', bottom: -80, left: -50, width: 280, height: 280, borderRadius: '50%', background: 'rgba(16,185,129,0.05)' }} />

          {/* Logo */}
          <div className="relative z-10 px-12 pt-10 pb-0">
            <Logo />
          </div>

          {/* Central content */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-16 py-8 text-center">
            <Lottie
              animationData={callCenterAnim}
              loop
              className="w-64 h-64 mb-8"
              style={{ opacity: 0.92 }}
            />

            <div style={fadeStyle} className="w-full max-w-md">
              <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-2">
                {isLogin
                  ? 'Envie mensagens no WhatsApp e aumente suas vendas'
                  : 'Crie sua conta gratuitamente'}
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-8">
                {isLogin
                  ? 'Automatize seu atendimento e alcance mais clientes em minutos.'
                  : 'Comece a disparar mensagens no WhatsApp em poucos minutos.'}
              </p>
              <ul className="space-y-3 inline-block text-left">
                {BULLETS.map(({ text }) => (
                  <li key={text} className="flex items-center gap-3 text-sm text-gray-600">
                    <span
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(16,185,129,0.15)' }}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="#059669" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-10 px-12 py-6 text-center">
            <p className="text-xs text-gray-400">© 2026 Disparador Pro. Todos os direitos reservados.</p>
          </div>
        </div>

      </div>
    </div>
  );
}
